import { useCallback, useMemo } from "react";
import { t } from "ttag";

import {
  skipToken,
  useGetMeasureQuery,
  useUpdateMeasureMutation,
} from "metabase/api";
import { useLoadTableWithMetadata } from "metabase/common/data-studio/hooks/use-load-table-with-metadata";
import { useMetadataToasts } from "metabase/metadata/hooks";
import { useNavigate } from "metabase/router";
import * as Urls from "metabase/urls";
import { getSchemaName } from "metabase-lib/v1/metadata/utils/schema";

import type { MeasureTabUrls } from "../types";

type DataModelMeasurePageParams = {
  databaseId: string;
  schemaId: string;
  tableId: string;
  measureId: string;
};

export function useDataModelMeasurePage(
  params: Partial<DataModelMeasurePageParams>,
) {
  const navigate = useNavigate();
  const { sendSuccessToast, sendErrorToast } = useMetadataToasts();
  const [updateMeasure] = useUpdateMeasureMutation();

  const databaseId = Number(params.databaseId);
  const schemaName = getSchemaName(params.schemaId);
  const tableId = Urls.extractEntityId(params.tableId);
  const measureId = Urls.extractEntityId(params.measureId);

  const {
    data: measure,
    isLoading: isLoadingMeasure,
    error: measureError,
  } = useGetMeasureQuery(measureId ?? skipToken);

  const {
    table,
    isLoading: isLoadingTable,
    error: tableError,
  } = useLoadTableWithMetadata(measure?.table_id, {
    includeForeignTables: true,
  });

  const handleRemove = useCallback(async () => {
    if (measure == null || tableId == null || schemaName == null) {
      return;
    }

    const { error } = await updateMeasure({
      id: measure.id,
      archived: true,
      revision_message: t`Removed from Data Studio`,
    });

    if (error) {
      sendErrorToast(t`Failed to remove measure`);
    } else {
      sendSuccessToast(t`Measure removed`);
      navigate(
        Urls.dataStudioData({
          databaseId,
          schemaName,
          tableId,
          tab: "measures",
        }),
      );
    }
  }, [
    measure,
    tableId,
    schemaName,
    databaseId,
    updateMeasure,
    sendSuccessToast,
    sendErrorToast,
    navigate,
  ]);

  const isLoading = isLoadingMeasure || isLoadingTable;
  const error = measureError ?? tableError;

  const tabUrls: MeasureTabUrls | null = useMemo(() => {
    if (tableId == null || schemaName == null || measureId == null) {
      return null;
    }
    const urlParams = { databaseId, schemaName, tableId, measureId };
    return {
      definition: Urls.dataStudioDataModelMeasure(urlParams),
      revisions: Urls.dataStudioDataModelMeasureRevisions(urlParams),
      dependencies: Urls.dataStudioDataModelMeasureDependencies(urlParams),
    };
  }, [databaseId, schemaName, tableId, measureId]);

  return {
    isLoading,
    error,
    measure: measure ?? null,
    table: table ?? null,
    tabUrls,
    onRemove: handleRemove,
  };
}
